import { readBrowserNotificationsUserEnabled } from '@/notifications/browserNotificationSettings';
import { getMessengerNotificationSurfaceState } from '@/notifications/messengerSurfaceState';

/** SW message: close notifications with the given tag (see client/src/sw.ts). */
export const MESSENGER_DISMISS_NOTIFICATION = 'MESSENGER_DISMISS_NOTIFICATION' as const;

const MESSENGER_THREAD_TAG_PREFIX = 'messenger-thread-';
const AGENT_CHAT_TAG_PREFIX = 'agent-chat-';

const pageNotificationsByTag = new Map<string, Notification>();

export type SystemNotificationInput = {
  title: string;
  body: string;
  /** Один тег на чат — новое сообщение заменяет предыдущее уведомление из того же треда. */
  tag: string;
  data?: Record<string, unknown>;
};

/** Tag used by page notifications and Web Push for a messenger thread. */
export function messengerThreadNotificationTag(threadId: string): string {
  return `${MESSENGER_THREAD_TAG_PREFIX}${threadId.trim()}`;
}

/** Tag used by page notifications and Web Push for an Astra agent chat. */
export function agentChatNotificationTag(chatId: string): string {
  return `${AGENT_CHAT_TAG_PREFIX}${chatId.trim()}`;
}

function tryHaptic(): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(120);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Показать уведомление с учётом разрешения и пользовательского флага.
 * Не бросает при ошибках API.
 */
export function showSystemNotification(input: SystemNotificationInput): Notification | null {
  if (typeof Notification === 'undefined') return null;
  if (!readBrowserNotificationsUserEnabled()) return null;
  if (Notification.permission !== 'granted') return null;
  try {
    tryHaptic();
    const notification = new Notification(input.title, {
      body: input.body,
      tag: input.tag,
      data: input.data,
    });
    pageNotificationsByTag.set(input.tag, notification);
    notification.onclose = () => {
      if (pageNotificationsByTag.get(input.tag) === notification) {
        pageNotificationsByTag.delete(input.tag);
      }
    };
    return notification;
  } catch {
    return null;
  }
}

async function closeNotificationsOnRegistration(
  registration: ServiceWorkerRegistration,
  tag: string
): Promise<void> {
  try {
    const notifications = await registration.getNotifications({ tag });
    for (const notification of notifications) {
      notification.close();
    }
  } catch {
    /* ignore */
  }
}

function closeTrackedPageNotification(tag: string): void {
  const notification = pageNotificationsByTag.get(tag);
  if (!notification) return;
  try {
    notification.close();
  } catch {
    /* ignore */
  }
  pageNotificationsByTag.delete(tag);
}

function postDismissToServiceWorker(tag: string): void {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
  try {
    const worker = navigator.serviceWorker.controller;
    worker?.postMessage({ type: MESSENGER_DISMISS_NOTIFICATION, tag });
  } catch {
    /* ignore */
  }
}

/**
 * Close browser/Web Push notifications for `tag` on this client.
 * Uses tracked page Notification, ServiceWorkerRegistration.getNotifications, and SW postMessage.
 */
export async function dismissNotificationsByTag(tag: string): Promise<void> {
  const normalized = tag.trim();
  if (!normalized) return;

  closeTrackedPageNotification(normalized);

  if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await closeNotificationsOnRegistration(registration, normalized);
    const worker = navigator.serviceWorker.controller ?? registration.active;
    worker?.postMessage({ type: MESSENGER_DISMISS_NOTIFICATION, tag: normalized });
  } catch {
    postDismissToServiceWorker(normalized);
  }
}

export function dismissMessengerThreadNotifications(threadId: string): void {
  const id = threadId.trim();
  if (!id) return;
  void dismissNotificationsByTag(messengerThreadNotificationTag(id));
}

export function dismissAgentChatNotifications(chatId: string): void {
  const id = chatId.trim();
  if (!id) return;
  void dismissNotificationsByTag(agentChatNotificationTag(id));
}

type UnreadEntity = { id: string; unread_count?: number | null };

/**
 * After reconnect / thread-list refresh: close notifications whose thread/chat
 * is already read on the server (covers offline read on another device).
 */
export function dismissNotificationsForReadEntities(params: {
  threads?: UnreadEntity[];
  agentChats?: UnreadEntity[];
}): void {
  if (typeof navigator === 'undefined') return;

  const zeroThreadIds = new Set(
    (params.threads ?? [])
      .filter((t) => (t.unread_count ?? 0) === 0)
      .map((t) => t.id.trim())
      .filter(Boolean)
  );
  const zeroAgentIds = new Set(
    (params.agentChats ?? [])
      .filter((c) => (c.unread_count ?? 0) === 0)
      .map((c) => c.id.trim())
      .filter(Boolean)
  );
  if (zeroThreadIds.size === 0 && zeroAgentIds.size === 0) return;

  void (async () => {
    const tagsToDismiss = new Set<string>();
    let registrationScanFailed = false;

    for (const [tag] of pageNotificationsByTag) {
      if (tag.startsWith(MESSENGER_THREAD_TAG_PREFIX)) {
        const id = tag.slice(MESSENGER_THREAD_TAG_PREFIX.length);
        if (zeroThreadIds.has(id)) tagsToDismiss.add(tag);
      } else if (tag.startsWith(AGENT_CHAT_TAG_PREFIX)) {
        const id = tag.slice(AGENT_CHAT_TAG_PREFIX.length);
        if (zeroAgentIds.has(id)) tagsToDismiss.add(tag);
      }
    }

    if (navigator.serviceWorker) {
      try {
        const registration = await navigator.serviceWorker.ready;
        try {
          const all = await registration.getNotifications();
          for (const n of all) {
            const tag = typeof n.tag === 'string' ? n.tag : '';
            if (tag.startsWith(MESSENGER_THREAD_TAG_PREFIX)) {
              const id = tag.slice(MESSENGER_THREAD_TAG_PREFIX.length);
              if (zeroThreadIds.has(id)) tagsToDismiss.add(tag);
            } else if (tag.startsWith(AGENT_CHAT_TAG_PREFIX)) {
              const id = tag.slice(AGENT_CHAT_TAG_PREFIX.length);
              if (zeroAgentIds.has(id)) tagsToDismiss.add(tag);
            }
          }
        } catch {
          registrationScanFailed = true;
        }
      } catch {
        registrationScanFailed = true;
      }
    }

    if (registrationScanFailed && tagsToDismiss.size === 0) {
      for (const id of zeroThreadIds) tagsToDismiss.add(messengerThreadNotificationTag(id));
      for (const id of zeroAgentIds) tagsToDismiss.add(agentChatNotificationTag(id));
    }

    for (const tag of tagsToDismiss) {
      await dismissNotificationsByTag(tag);
    }
  })();
}

/**
 * Уведомление о сообщении мессенджера: не показываем, если мессенджер уже открыт.
 */
export function shouldSuppressMessengerMessageNotification(params: {
  threadId: string;
  senderPersonId: string;
  currentUserPersonId: string | null;
}): boolean {
  const { senderPersonId, currentUserPersonId } = params;
  if (!currentUserPersonId || senderPersonId === currentUserPersonId) return true;

  if (typeof document === 'undefined') return false;
  if (document.visibilityState !== 'visible') return false;

  const { popoverOpen } = getMessengerNotificationSurfaceState();
  if (popoverOpen) return true;

  return false;
}

/** Уведомление ответа Астры: подавляем только когда открыт именно этот чат Астры. */
export function shouldSuppressAgentChatNotification(params: { chatId: string }): boolean {
  const { chatId } = params;
  if (!chatId.trim()) return false;
  if (typeof document === 'undefined') return false;
  if (document.visibilityState !== 'visible') return false;
  const { popoverOpen, agentPaneActive, activeAgentChatId } = getMessengerNotificationSurfaceState();
  if (popoverOpen && agentPaneActive && activeAgentChatId === chatId) return true;
  return false;
}

export function attachNotificationClickNavigate(
  notification: Notification | null,
  onNavigate: () => void
): void {
  if (!notification) return;
  notification.onclick = () => {
    try {
      window.focus();
    } catch {
      /* ignore */
    }
    onNavigate();
    notification.close();
  };
}
